
import { HttpClient } from '@/api/services/http';
import { Chatbot } from '@/types/chatbot';
import { WorkflowRuleTable } from './workflowService';
import { ChatBotRuleFieldsResponse, ChatBotRuleTable } from '@/components/automation/chatbots/form/audience-rules/hooks/useAudienceFields';
import type { QueryGroup } from '@/types/queryBuilder';

interface AudienceEstimateResponse {
  entity: string;
  totalMatches: number;
}

interface AudienceSampleResponse {
  entity: string;
  matches: any[];
}

const getWorkspaceId = () => {
  return localStorage.getItem('workspaceId');
};
interface ChatbotResponse {
  data: Chatbot[];
  total: number;
  page: number;
  limit: number;
}
export const ChatbotService = {
  async createChatbot(chatbot: Partial<Chatbot>): Promise<Chatbot> {
    const response = await HttpClient.apiClient.post('/chatbot-profile', {
      ...chatbot,
      status: chatbot.status || (chatbot.isActive ? 'active' : 'inactive'),
      channels: chatbot.channels || ['chat'],
      audienceRules: chatbot.audienceRules || { combinator: 'and', rules: [] },
    });
    const payload = (response.data?.data ?? response.data) as Chatbot;
    return payload;
  },

  async updateChatbot(id: string, chatbot: Partial<Chatbot>): Promise<Chatbot> {
    const response = await HttpClient.apiClient.put<Chatbot>(`/chatbot-profile/${id}`, chatbot);
    return response.data?.data ?? response.data;
  },

  async getChatbot(id: string): Promise<Chatbot> {
    const response = await HttpClient.apiClient.get<Chatbot>(`/chatbot-profile/${id}`);
    return response.data?.data ?? response.data;
  },

  async getChatbotRuleFields(): Promise<ChatBotRuleFieldsResponse> {
    const response = await HttpClient.apiClient.get<ChatBotRuleFieldsResponse>('/chatbot-profile/rule-fields');
    return response.data;
  },

  async fetchChatbots(): Promise<ChatbotResponse> {
    const response = await HttpClient.apiClient.get<ChatbotResponse>('/chatbot-profile');
    return response.data;
  },

  async estimateAudience(
    query: QueryGroup,
    entity: string = 'contacts'
  ): Promise<AudienceEstimateResponse> {
    const workspaceId = getWorkspaceId();
    if (!workspaceId) {
      throw new Error('Workspace ID is required to estimate audience size.');
    }

    const requestedEntity = entity ?? 'contacts';
    const normalizedEntity = requestedEntity === 'contacts' ? 'customers' : requestedEntity;

    const response = await HttpClient.apiClient.post<{ data: AudienceEstimateResponse }>(
      '/chatbot-profile/audience/estimate',
      {
        workspaceId,
        entity: normalizedEntity,
        query,
      }
    );

    return response.data.data;
  },

  async getAudienceSample(
    query: QueryGroup,
    options: { entity?: string; limit?: number; fields?: string[] } = {}
  ): Promise<AudienceSampleResponse> {
    const workspaceId = getWorkspaceId();
    if (!workspaceId) {
      throw new Error('Workspace ID is required to fetch audience samples.');
    }

    const requestedEntity = options.entity ?? 'contacts';
    const normalizedEntity = requestedEntity === 'contacts' ? 'customers' : requestedEntity;

    const response = await HttpClient.apiClient.post<{ data: AudienceSampleResponse }>(
      '/chatbot-profile/audience/sample',
      {
        workspaceId,
        entity: normalizedEntity,
        query,
        limit: options.limit,
        fields: options.fields,
      }
    );

    return response.data.data;
  },
};
